import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Bell, Camera } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 3 seconds on first visit
      const hasSeenPrompt = localStorage.getItem('pwa-prompt-seen');
      if (!hasSeenPrompt) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show prompt after delay
    if (iOS) {
      const hasSeenPrompt = localStorage.getItem('pwa-prompt-seen');
      if (!hasSeenPrompt) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-seen', 'true');
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-seen', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 rounded-2xl p-4 backdrop-blur-xl shadow-2xl">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">Install ANANT App</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Get quick access, push notifications, and offline support!
            </p>
            
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs bg-white/10 px-2 py-1 rounded-full flex items-center gap-1">
                <Bell className="w-3 h-3" /> Notifications
              </span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded-full flex items-center gap-1">
                <Camera className="w-3 h-3" /> Camera
              </span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded-full flex items-center gap-1">
                <Download className="w-3 h-3" /> Offline
              </span>
            </div>
            
            {isIOS ? (
              <div className="text-xs text-muted-foreground">
                <p>Tap <span className="inline-block w-4 h-4 bg-white/20 rounded text-center align-middle">⬆</span> then "Add to Home Screen"</p>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                className="w-full btn-primary text-sm py-2 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Install Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
